import { type Platforms } from 'shared/api/accounts'
import { APP_ROUTES } from 'shared/routes'

/** ADK agent id for the Flow data agent (`precis_agent_v1` in the Flow repo). */
export const FLOW_DATA_AGENT_ID = 'precis_agent_v1' as const

export const STANDALONE_DATA_AGENT_ROUTE = APP_ROUTES.copilot.chatPage

export const isStandaloneDataAgentRoute = (pathname: string): boolean =>
  pathname === STANDALONE_DATA_AGENT_ROUTE || pathname.startsWith(`${STANDALONE_DATA_AGENT_ROUTE}/`)

/** Native OAuth ad platforms (Alvie platform → Flow entity type + suggestion-card label). */
export const OAUTH_ADS_FLOW_DATASOURCES = [
  { alviePlatform: 'facebook', entityType: 'meta_ads_account', sourceLabel: 'Meta Ads' },
  { alviePlatform: 'linkedin', entityType: 'linkedin_ads_account', sourceLabel: 'LinkedIn Ads' },
  { alviePlatform: 'tiktok', entityType: 'tiktok_ads_account', sourceLabel: 'TikTok Ads' },
  { alviePlatform: 'snapchat', entityType: 'snapchat_ads_account', sourceLabel: 'Snapchat Ads' },
  { alviePlatform: 'pinterest', entityType: 'pinterest_ads_account', sourceLabel: 'Pinterest Ads' }
] as const

export type OauthAdsAlviePlatform = (typeof OAUTH_ADS_FLOW_DATASOURCES)[number]['alviePlatform']

export const FLOW_DATASOURCE_ENTITY_TYPES = [
  'google_ads_account',
  'ga4_property',
  'gsc_property',
  ...OAUTH_ADS_FLOW_DATASOURCES.map((entry) => entry.entityType)
] as const

export type FlowDatasourceEntityType = (typeof FLOW_DATASOURCE_ENTITY_TYPES)[number]

const oauthEntityTypeToPlatform = Object.fromEntries(
  OAUTH_ADS_FLOW_DATASOURCES.map((entry) => [entry.entityType, entry.alviePlatform])
) as Record<
  (typeof OAUTH_ADS_FLOW_DATASOURCES)[number]['entityType'],
  (typeof OAUTH_ADS_FLOW_DATASOURCES)[number]['alviePlatform']
>

/** Alvie account platform behind each Flow datasource entity type. */
export const FLOW_DATASOURCE_ENTITY_TYPE_TO_PLATFORM: Record<FlowDatasourceEntityType, Platforms> = {
  google_ads_account: 'google',
  ga4_property: 'ga4',
  gsc_property: 'google_search_console',
  ...oauthEntityTypeToPlatform
}

/**
 * Platforms the Flow data agent can run against today.
 * Native Pinterest (`pinterest`) only — Fivetran-backed `pinterest_ads` stays unsupported.
 */
export const SUPPORTED_FLOW_DATASOURCE_PLATFORMS = [
  'google',
  'ga4',
  'google_search_console',
  ...OAUTH_ADS_FLOW_DATASOURCES.map((entry) => entry.alviePlatform)
] as const

export type SupportedFlowDatasourcePlatform = (typeof SUPPORTED_FLOW_DATASOURCE_PLATFORMS)[number]

export const isSupportedFlowDatasourcePlatform = (platform: string): platform is SupportedFlowDatasourcePlatform => {
  return (SUPPORTED_FLOW_DATASOURCE_PLATFORMS as readonly string[]).includes(platform)
}

/** Flow agent entity shape (session `entities` payload). */
export interface FlowDatasourceEntity {
  id: string
  type: FlowDatasourceEntityType
  name: string
  data: Record<string, string | boolean | null | undefined>
}

export interface FlowDataAgentSessionState {
  entities: FlowDatasourceEntity[]
  external_workspace_id: number
}
